'use client';

import type { MouseEvent } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Baby,
  User,
  Heart,
  ChevronRight,
  ExternalLink,
  Check,
  Loader2,
} from 'lucide-react';
import type { FinderGroup, MyGroup, MyInquiry } from './types';
import { campusShort, leaderLine } from './format';
import { AvatarStack } from './Avatar';
import { CampusIcon } from './CampusIcon';
import { Tag, StatusBadge, MineBadge, InquiryBadge } from './chips';
import { SeeJesusButton, SeeJesusCorner, SeeJesusFrame, SeeJesusTag } from './SeeJesus';
import { SEE_JESUS_COPY } from './seeJesusBrand';
import { toPlainText } from './richText';

interface GroupCardProps {
  group: FinderGroup;
  detailsUrl: string;
  /** Present when the signed-in visitor is in this group */
  mine?: MyGroup;
  /** Recent inquiries into this group by the visitor's household */
  inquiries?: MyInquiry[];
  /**
   * Opens the group in a sheet (the finder). When omitted the whole card is
   * a link to the group's details page (search results and other listings).
   */
  onOpen?: (group: FinderGroup) => void;
  /** Opens the "what is a See Jesus Group?" explainer */
  onExplainSeeJesus?: () => void;
  /** A request about this group is in flight: the card dims and the button says what is happening */
  busy?: string;
  /** Draw the eye to this card for a moment (it just moved here) */
  highlight?: boolean;
}

const ICON = 'h-4 w-4 shrink-0 text-gray-400';
const TAG_ICON = 'h-3.5 w-3.5 text-gray-500';

export function GroupTags({
  group,
  dark = false,
  onSeeJesus,
}: {
  group: FinderGroup;
  dark?: boolean;
  /** Opens the "what is a See Jesus Group?" explainer from the branded tag */
  onSeeJesus?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {group.campusName && (
        <Tag dark={dark} icon={<CampusIcon congregationId={group.campusId} className="h-4 w-4" />}>
          {campusShort(group.campusName)}
        </Tag>
      )}
      {group.lifeStage && (
        <Tag dark={dark} icon={<Users className={TAG_ICON} />}>
          {group.lifeStage}
        </Tag>
      )}
      {group.kidsWelcome === true && (
        <Tag dark={dark} icon={<Baby className={TAG_ICON} />}>
          Kids welcome
        </Tag>
      )}
      {group.kidsWelcome === false && (
        <Tag dark={dark} icon={<User className={TAG_ICON} />}>
          Adults only
        </Tag>
      )}
      {group.householdType && (
        <Tag dark={dark} icon={<Heart className={TAG_ICON} />}>
          {group.householdType}
        </Tag>
      )}
      {group.gender && (
        <Tag dark={dark} icon={<User className={TAG_ICON} />}>
          {group.gender}
        </Tag>
      )}
      {group.seeJesus && <SeeJesusTag onClick={onSeeJesus} />}
    </div>
  );
}

export function GroupCard({
  group,
  detailsUrl,
  mine,
  inquiries,
  onOpen,
  onExplainSeeJesus,
  busy,
  highlight = false,
}: GroupCardProps) {
  const joinable = !group.full && !group.paused;
  // One badge for the household: waiting beats placed beats closed.
  const inq =
    !mine && inquiries?.length
      ? inquiries.some((i) => i.status === 'pending')
        ? 'pending'
        : inquiries.some((i) => i.status === 'placed')
          ? 'placed'
          : 'closed'
      : null;
  // See Jesus Groups wear the campaign's brand: black card, cream type, gold.
  const sj = group.seeJesus;
  const ink = sj ? 'text-[#f6efe9]' : 'text-[#1c2b39]';
  const muted = sj ? 'text-[#f6efe9]/70' : 'text-gray-600';
  const href = `${detailsUrl}${group.id}`;
  // Link mode: the card itself navigates, so nothing inside may be an anchor.
  const linkMode = !onOpen;

  function stop(e: MouseEvent) {
    e.stopPropagation();
  }
  // Inquire opens the sheet (the details live there now); a modified click
  // or "open in new tab" still gets the group's own page.
  function inquire(e: MouseEvent) {
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    onOpen?.(group);
  }

  const cta =
    'inline-flex w-full shrink-0 items-center justify-center gap-1 rounded-full px-5 py-2.5 text-sm font-bold no-underline shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95';
  const ctaStatic =
    'inline-flex w-full shrink-0 items-center justify-center gap-1 rounded-full px-5 py-2.5 text-sm font-bold shadow-sm transition-all group-hover:scale-105 group-hover:shadow-md';

  const interactive = linkMode
    ? {}
    : {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onOpen(group),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(group);
          }
        },
      };

  const content = (
    <>
      <div className={`flex items-start justify-between gap-3 ${sj ? 'pr-20 sm:pr-24' : ''}`}>
        <div className="flex min-w-0 flex-col gap-2">
          <h3
            className={
              sj
                ? 'sj-foil-text sj-display text-2xl leading-none font-normal tracking-normal normal-case sm:text-3xl'
                : `text-lg leading-tight font-extrabold tracking-tight uppercase sm:text-xl ${ink}`
            }
          >
            {group.title}
          </h3>
          {sj && (mine || inq || group.full || group.paused) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {mine && <MineBadge role={mine.role} />}
              {inq && <InquiryBadge status={inq} />}
              <StatusBadge full={group.full} paused={group.paused} />
            </div>
          )}
        </div>
        {!sj && (
          <div className="flex shrink-0 items-center gap-1.5">
            {mine && <MineBadge role={mine.role} />}
            {inq && <InquiryBadge status={inq} />}
            <StatusBadge full={group.full} paused={group.paused} />
          </div>
        )}
      </div>

      <div className="grid gap-4">
        <div className={`flex min-w-0 flex-col gap-2 text-sm ${ink}`}>
          <div className="flex items-center gap-2">
            <Calendar className={ICON} />
            <span className="font-medium">{group.occurrence || group.day}</span>
          </div>
          {group.time && (
            <div className="flex items-center gap-2">
              <Clock className={ICON} />
              <span>{group.time}</span>
            </div>
          )}
          {(mine?.address?.line1 || group.city) && (
            <div className="flex items-center gap-2">
              <MapPin className={ICON} />
              <span>
                {mine?.address?.line1
                  ? `${mine.address.line1}, ${mine.address.city ?? group.city}`
                  : group.city}
              </span>
            </div>
          )}
          {group.leaders.length > 0 && (
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <AvatarStack people={group.leaders} size={28} />
              <span className={`min-w-0 flex-1 text-sm leading-snug ${muted}`}>
                {leaderLine(group.leaders.map((l) => l.name))}
              </span>
            </div>
          )}
        </div>

        <div className="min-w-0">
          {group.description || sj ? (
            <p className={`line-clamp-3 text-sm leading-relaxed whitespace-pre-line ${muted}`}>
              {group.description ? toPlainText(group.description) : SEE_JESUS_COPY.lead}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">No description yet.</p>
          )}
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-3">
        <GroupTags group={group} dark={sj} onSeeJesus={onExplainSeeJesus} />
        {busy ? (
          <span
            className={`${ctaStatic} ${sj ? 'bg-white/10 text-[#f6efe9]/80' : 'bg-gray-100 text-gray-500'}`}
          >
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
            {busy}
          </span>
        ) : mine && !linkMode ? (
          <a
            href={mine.connectUrl}
            target="_blank"
            rel="noopener"
            onClick={stop}
            className={`${cta} bg-[#1c2b39] text-white`}
          >
            Open in Group Connect
            <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
          </a>
        ) : inq && inq !== 'closed' && !linkMode ? (
          <a
            href={href}
            onClick={inquire}
            className={`${cta} ${sj ? 'bg-white/10 text-[#f6efe9] ring-1 ring-white/20' : 'bg-gray-100 text-[#1c2b39]'}`}
          >
            {inq === 'placed' ? 'Placed' : 'Inquiry sent'}
            <Check className="h-4 w-4" strokeWidth={3} />
          </a>
        ) : joinable ? (
          sj ? (
            linkMode ? (
              <SeeJesusButton as="span" className="w-full sm:w-auto">
                Inquire
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </SeeJesusButton>
            ) : (
              <SeeJesusButton href={href} onClick={inquire} className="w-full sm:w-auto">
                Inquire
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </SeeJesusButton>
            )
          ) : linkMode ? (
            <span className={`${ctaStatic} bg-[#299c8f] text-white`}>
              Inquire
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </span>
          ) : (
            <a href={href} onClick={inquire} className={`${cta} bg-[#299c8f] text-white`}>
              Inquire
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          )
        ) : (
          <span
            className={`inline-flex shrink-0 items-center gap-1 text-sm font-semibold ${sj ? 'text-[#f6efe9]/70' : 'text-gray-500'}`}
          >
            Details
            <ChevronRight className="h-4 w-4" />
          </span>
        )}
      </div>
    </>
  );

  const Outer: 'a' | 'article' = linkMode ? 'a' : 'article';
  const outerProps = { ...(linkMode ? { href } : interactive), 'data-group-id': group.id };
  // Busy: no clicks, dimmed. Highlight: a teal ring that fades once the
  // caller drops it (the transition is on the ring classes).
  const state = `${busy ? 'pointer-events-none opacity-60' : ''} ${
    highlight ? 'ring-2 ring-[#299c8f] ring-offset-2' : ''
  }`;

  if (sj) {
    // The campaign's frame: double gold border, black pane, logo top right.
    return (
      <Outer
        {...outerProps}
        className={`group block h-full cursor-pointer rounded-2xl text-left text-inherit no-underline shadow-sm transition-all hover:scale-[1.005] hover:shadow-md active:scale-[0.995] ${
          mine ? 'ring-2 ring-[#c79854]/50 ring-offset-2' : ''
        } ${state}`}
      >
        <SeeJesusFrame
          className="flex h-full flex-col"
          paneClassName="relative flex flex-1 flex-col gap-4 p-5 sm:p-6"
          corner={<SeeJesusCorner />}
        >
          {content}
        </SeeJesusFrame>
      </Outer>
    );
  }

  return (
    <Outer
      {...outerProps}
      className={
        'group flex h-full cursor-pointer flex-col gap-4 rounded-2xl border bg-white p-5 text-left text-inherit no-underline shadow-sm transition-all hover:scale-[1.005] hover:shadow-md active:scale-[0.995] sm:p-6 ' +
        (mine ? 'border-[#299c8f]/50 ring-1 ring-[#299c8f]/30' : 'border-gray-200') +
        ' ' +
        state
      }
    >
      {content}
    </Outer>
  );
}
